import { iconAlertaErroPreenchido } from '@bsds-lib-component/icons/icons';
import { BsdsIcon, BsdsModal } from '@bsds-lib-component/react';
import React, { useId } from 'react';

import { Button, ButtonClose } from '../../..';
import type { SeguradoDataReturn } from '../../../../types/vida/maintenanceSegurado/maintenanceSeguradoType';

type SeguradoInconsistencyModalProps = {
  isOpen: boolean;
  inconsistencies: SeguradoDataReturn[];
  onClose: () => void;
};

const SummaryField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <span className="text-xs text-gray-500 block mb-1">{label}</span>
    <span className="text-sm font-semibold text-gray-900 break-words block">{value}</span>
  </div>
);

const resolveInconsistencyMessage = (item: SeguradoDataReturn): string =>
  item.critica?.trim() || item.descrCritica?.trim() || '—';

const SeguradoInconsistencyModal: React.FC<SeguradoInconsistencyModalProps> = ({
  isOpen,
  inconsistencies,
  onClose,
}) => {
  const modalId = useId();
  const totalInconsistencies = inconsistencies.length;

  return (
    <BsdsModal
      isOpen={isOpen}
      onBsdsModalDidDismiss={onClose}
      backdropDismiss
      color="secondary"
      canDismiss
      animated
      showBackdrop
      keepContentsMounted
      platform="web"
      aria-labelledby={modalId}
      aria-describedby={`${modalId}-description`}
    >
      <div
        className="modal-content flex flex-col h-screen max-h-screen md:h-[75vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalId}
      >
        <div className="relative flex items-center justify-center p-5 pb-4 shrink-0 border-b border-gray-100">
          <div className="absolute top-4 right-4">
            <ButtonClose isModal onClick={onClose} />
          </div>
          <h5 id={modalId} className="text-lg font-semibold text-gray-900 text-center">
            Inconsistências no cadastro
          </h5>
        </div>

        <div
          id={`${modalId}-description`}
          className="overflow-y-auto custom-scrollbar flex-1 px-5 py-4 min-h-0 space-y-6"
          role="region"
          aria-label="Detalhes das inconsistências encontradas"
        >
          <div className="rounded-lg bg-red-50 border border-red-100 p-4 flex items-start gap-3">
            <BsdsIcon
              icon={iconAlertaErroPreenchido}
              className="w-5 h-5 text-red-600 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Não foi possível salvar o segurado
              </p>
              <p className="text-sm text-red-700 mt-1">
                {totalInconsistencies === 1
                  ? 'Foi encontrada 1 inconsistência que precisa ser corrigida antes de continuar.'
                  : `Foram encontradas ${totalInconsistencies} inconsistências que precisam ser corrigidas antes de continuar.`}
              </p>
            </div>
          </div>

          {totalInconsistencies === 1 ? (
            <div className="rounded-lg bg-gray-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <SummaryField
                label="Campo"
                value={inconsistencies[0].nmCampo?.trim() || '—'}
              />
              <SummaryField
                label="Registro"
                value={inconsistencies[0].linha != null ? String(inconsistencies[0].linha) : '—'}
              />
              <div className="sm:col-span-2">
                <SummaryField
                  label="Tipo de inconsistência"
                  value={resolveInconsistencyMessage(inconsistencies[0])}
                />
              </div>
              {inconsistencies[0].conteudoCampo?.trim() && (
                <div className="sm:col-span-2">
                  <SummaryField label="Conteúdo" value={inconsistencies[0].conteudoCampo.trim()} />
                </div>
              )}
            </div>
          ) : (
            <section aria-label="Lista de inconsistências">
              <div className="flex items-center mb-4">
                <div className="w-[2.5px] h-6 bg-red-600 mr-3" aria-hidden="true" />
                <h6 className="text-base font-semibold opacity-80">Inconsistências encontradas</h6>
              </div>

              <div className="w-full overflow-x-auto custom-scrollbar rounded-md border border-gray-100">
                <table className="min-w-[640px] w-full table-fixed" aria-label="Inconsistências do segurado">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-20" scope="col">
                        Registro
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-40" scope="col">
                        Campo
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100" scope="col">
                        Tipo de inconsistência
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-40" scope="col">
                        Conteúdo
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-text-color text-xs">
                    {inconsistencies.map((item, index) => (
                      <tr key={`${item.linha ?? 'row'}-${index}`}>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {item.linha ?? '—'}
                        </td>
                        <td className="px-3 py-2 break-words">{item.nmCampo?.trim() || '—'}</td>
                        <td className="px-3 py-2 break-words font-medium text-red-700">
                          {resolveInconsistencyMessage(item)}
                        </td>
                        <td className="px-3 py-2 break-words">{item.conteudoCampo?.trim() || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <div className="p-5 pt-4 shrink-0 border-t border-gray-100 bg-white">
          <Button
            type="button"
            color="secondary"
            fill="outline"
            className="bsds-width-100"
            onClick={onClose}
            aria-label="Fechar modal de inconsistências"
          >
            Fechar
          </Button>
        </div>
      </div>
    </BsdsModal>
  );
};

export default SeguradoInconsistencyModal;
